import { STATUS_FAIL } from '@/mixins/onlineCheckMixin';

/**
 * @description Примесь изменения способа оплаты.
 * @type {Vue}
 */
const changePaymentModeMixin = {
  computed: {
    /**
     * @description Показать диалог изменения способа оплаты для операции по счету.
     */
    isShownChangePaymentModeForAdvanceRechargeOperationDialog() {
      return this.rechargeOperationForChangeMode !== null;
    },
  },
  data() {
    return {
      /**
       * @description Операция изменения способа пополнения аванса.
       * @type {(object|null)}
       */
      rechargeOperationForChangeMode: null,
      /**
       * @description Доступные способы оплаты для смены.
       * @type {object[]}
       */
      availablePaymentModesForRechargeOperationChanging: [],
    };
  },
  methods: {
    /**
     * @description Обработать отмену изменения способа оплаты для операции пополнения аванса.
     * @param {boolean} needsShown Нужно показывать.
     * @returns {void}
     */
    onCancelChangePaymentModeForAdvanceRechargeOperation(needsShown) {
      this.rechargeOperationForChangeMode = needsShown
        ? this.rechargeOperationForChangeMode
        : null;
    },
    /**
     * @description Обработать изменения способа оплаты для операции пополнения аванса.
     * @param {number} operationId Идентификатор операции.
     * @returns {void}
     */
    onChangePaymentModeFromAdvanceRechargeOperation(operationId) {
      this
        .$invoiceGateway
        .viewPaymentModeChangeForAdvanceRechargeOperation(operationId)
        .then((result) => {
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
          result.forEach(({ availableModes, rechargeOperation }) => {
            this.availablePaymentModesForRechargeOperationChanging = availableModes;
            this.rechargeOperationForChangeMode = rechargeOperation;
          });
        });
    },
    /**
     * @description Обработать отправку изменения способа оплаты для операции пополнения аванса.
     * @param {string} mode Способ оплаты.
     * @returns {void}
     */
    onSubmitChangePaymentModeForAdvanceRechargeOperation(mode) {
      this
        .$invoiceGateway
        .changePaymentModeForAdvanceRechargeOperation(
          this.rechargeOperationForChangeMode.id,
          mode,
        )
        .then((result) => {
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
          result.forEach(({ title }) => {
            this.onAdvanceDetailsOpen();
            this.onCancelChangePaymentModeForAdvanceRechargeOperation(false);
            this.notifySuccess(title);
          });
        });
    },
  },
};

/**
 * @description Примесь пополнения аванса.
 * @type {object}
 */
export default {
  data() {
    return {
      /**
       * @description Показывать форму пополнения аванса.
       * @type {boolean}
       */
      isShownAdvanceRechargeForm: false,
      /**
       * @description Ожидание завершения запроса пополнения аванса.
       * @type {boolean}
       */
      isAwaitAdvanceRechargeRequest: false,
      /**
       * @description Идентификатор команды пополнения.
       * @type {(number|null)}
       */
      rechargeCommandId: null,
    };
  },
  methods: {
    /**
     * @description Ожидание распечатки чека.
     * @type {number} commandId Идентификатор команды.
     * @returns {Promise<boolean>}
     */
    waitAdvanceRechargeCheckPrinted(commandId) {
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        return false;
      };
      const onSuccess = (result) => {
        if (result.status === STATUS_FAIL) {
          this.notifyFail(result.message);
          return false;
        }
        this.onCloseAdvanceRechargeForm();
        this.notifySuccess('Чек получен');
        return true;
      };
      return this
        .waitExecuted(commandId)
        .then(onSuccess, onFail)
        .finally(() => {
          this.isAwaitCommandExecuted = false;
        });
    },
    /**
     * @description Открыть форму.
     * @returns {void}
     */
    openAdvanceRechargeForm() {
      this.isShownAdvanceRechargeForm = true;
    },
    /**
     * @description Обработать закрытие формы.
     * @returns {void}
     */
    onCloseAdvanceRechargeForm() {
      this.isShownAdvanceRechargeForm = false;
    },
    /**
     * @description Отправить запрос на пополнение аванса.
     * @param {AdvanceRechargeRequest} request Запрос.
     * @returns {Promise<boolean>}
     */
    sendAdvanceRechargeRequest(request) {
      this.isAwaitAdvanceRechargeRequest = true;
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        return false;
      };
      const onSuccess = (response) => {
        this.notifySuccess(response.message);
        if (response.command_id) {
          this.rechargeCommandId = response.command_id;
          return this.waitAdvanceRechargeCheckPrinted(this.rechargeCommandId);
        }
        this.onCloseAdvanceRechargeForm();
        return true;
      };
      return this
        .$invoiceGateway
        .advanceRecharge(request)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitAdvanceRechargeRequest = false; });
    },
  },
  mixins: [changePaymentModeMixin],
};
